/**
 * What the shell looked like as the page left (issue 533), for the boot frame of
 * the next one.
 *
 * The boot frame (bootFrame.css, painted by each app's index.html) stands in for
 * AppShell until the app has resolved its first route. Two parts of the shell are
 * not known before paint: the strip above it (the demo stage bar) and a docked
 * Lumi panel — both appear only once the app has read its data. A frame that drew
 * neither made the panel jump the moment the real shell replaced it, on exactly
 * the reload that made the issue visible: a demo persona switch. So AppShell
 * records their size on `pagehide`, and the frame reserves the same space on the
 * next load.
 *
 * Remembered per origin in localStorage, like `lumi:sidebarCollapsed`; the suite
 * shares one origin per tenant in production, so a module switch carries it too.
 * It is a hint, not state: a stale value costs one shift, never a wrong screen.
 */
export const SHELL_LAYOUT_KEY = 'lumi:shellLayout';

export interface ShellLayout {
  /** Height of AppShell's `#top` strip in px; 0 when there is none. */
  top: number;
  /** Width of AppShell's `#dock` region in px; 0 when nothing is docked. */
  dock: number;
}

export function rememberShellLayout(layout: ShellLayout): void {
  if (typeof window === 'undefined') return;
  const value: ShellLayout = { top: Math.round(layout.top), dock: Math.round(layout.dock) };
  try {
    if (!value.top && !value.dock) localStorage.removeItem(SHELL_LAYOUT_KEY);
    else localStorage.setItem(SHELL_LAYOUT_KEY, JSON.stringify(value));
  } catch {
    // Storage can be unavailable (private mode, blocked site data): the next
    // boot frame just draws the plain shell.
  }
}
